import { create } from 'zustand'
import type { SilentAdventureResult } from '@api/endpoints/profile'

/**
 * Bridges the non-React location-tracking interval service to the React toast
 * layer in MainLayout. The tracking service runs on a 30s setInterval and
 * cannot render UI itself, so it pushes any silently joined/completed adventures
 * here; MainLayout subscribes to the queue and shows an acknowledgement toast.
 *
 * Adventures are deduped by id for the lifetime of the session: the backend
 * already stops reporting an adventure once it's completed, and this guards
 * against the same discovery being acknowledged twice across rapid pings.
 */
interface SilentAdventureAckState {
  /** Silent adventures awaiting an in-app acknowledgement toast, FIFO. */
  queue: SilentAdventureResult[]
  /** Adventure ids already acknowledged this session (dedupe across pings). */
  acknowledged: Set<string>
  /** Enqueue freshly joined/completed silent adventures, skipping duplicates. */
  enqueue: (results: SilentAdventureResult[]) => void
  /** Drop the head of the queue once its toast has been shown/dismissed. */
  shift: () => void
}

export const useSilentAdventureStore = create<SilentAdventureAckState>((set, get) => ({
  queue: [],
  acknowledged: new Set<string>(),
  enqueue: (results) => {
    const nextAck = new Set(get().acknowledged)
    const fresh: SilentAdventureResult[] = []
    // Dedupe both within this batch (an adventure can appear in both the joined
    // and completed lists) and against everything acknowledged this session.
    for (const r of results) {
      if (!r.adventureId || nextAck.has(r.adventureId)) continue
      nextAck.add(r.adventureId)
      fresh.push(r)
    }
    if (fresh.length === 0) return
    set((s) => ({ queue: [...s.queue, ...fresh], acknowledged: nextAck }))
  },
  shift: () => set((s) => ({ queue: s.queue.slice(1) })),
}))
